// src/portal/portalMapStyle.js
//
// What both portal maps share: the tokenless basemap, and the submission
// overlay. One file so the two maps cannot drift about what a submitted feature
// looks like — a reviewer and a submitter arguing about a pad edge must be
// looking at the same rendering of it.

/**
 * The tokenless neutral basemap, transcribed from
 * PlatformCoverageAoiMap.jsx:210-259 rather than imported from it: that
 * component owns an AOI-drawing surface with a grid, a snap handler and a
 * toolbar, and importing it here to reach a style object would pull all of it.
 *
 * Esri's public ArcGIS raster services. No token, no tile session, no
 * entitlement — which is what lets a third-party submitter render a map at all.
 */
export const ESRI_NEUTRAL_STYLE = Object.freeze({
  version: 8,
  sources: {
    "esri-world-imagery": {
      type: "raster",
      tiles: ["https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"],
      tileSize: 256,
    },
    "esri-transportation": {
      type: "raster",
      tiles: ["https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"],
      tileSize: 256,
    },
    "esri-boundaries": {
      type: "raster",
      tiles: ["https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"],
      tileSize: 256,
    },
  },
  layers: [
    { id: "esri-world-imagery", type: "raster", source: "esri-world-imagery" },
    { id: "esri-transportation", type: "raster", source: "esri-transportation", paint: { "raster-opacity": 0.65 } },
    { id: "esri-boundaries", type: "raster", source: "esri-boundaries", paint: { "raster-opacity": 0.8 } },
  ],
});

export const SUBMISSION_SOURCE = "portal-submission";

/** The three layers that draw a submitted feature, in the order they are added. */
export const SUBMISSION_LAYER_IDS = Object.freeze([
  `${SUBMISSION_SOURCE}-fill`,
  `${SUBMISSION_SOURCE}-line`,
  `${SUBMISSION_SOURCE}-point`,
]);

// ── UNTAGGED IS NOT `other`, AND FOR ONE COMMIT IT WAS ──────────────────────
//
// The expression was:
//
//   ["match", ["get","submitter_tag"], "pad", "#38bdf8", "road", "#fbbf24",
//    "#a3a3a3"]
//
// `other` had no arm of its own; it reached the FALLBACK. So `other`, an
// explicit null, and an absent property all evaluated to the same grey —
// measured with MapLibre's own evaluator, not reasoned:
//
//   pad -> rgba(56,189,248,1)    other     -> rgba(163,163,163,1)
//   road -> rgba(251,191,36,1)   null      -> rgba(163,163,163,1)
//                                undefined -> rgba(163,163,163,1)
//
// That was survivable while the map only ever showed a SUBMITTED revision,
// where every feature carries a tag. It stops being survivable the moment the
// map shows features being tagged: a submitter looking at a fully grey map
// would reasonably believe they had tagged everything `other`, and `other` is a
// real assertion in a record the reviewer relies on.
//
// So `other` gets an arm of its own, at THE COLOUR IT ALREADY HAD — the fix is
// to move untagged, not to move `other`. The tests pin all three tag colours
// precisely so "they differ now" cannot be satisfied by moving the wrong one.
//
// ── UNTAGGED IS DERIVED FROM THE VOCABULARY, NOT LISTED AGAIN ───────────────
//
// `IS_UNTAGGED` is "not one of SUBMITTER_TAGS", built from the imported array,
// so the style cannot drift from the vocabulary the server enforces. An
// unrecognised value folds into untagged, which is the honest reading: it is
// not an assertion we recognise, so the map must not claim it is `other`.

import { SUBMITTER_TAGS } from "./portalTags.js";
import { geometryColours } from "./portalMapTokens.js";

// ── THE COLOURS COME FROM THE TOKEN LAYER NOW ──────────────────────────────
//
// They were five module-level hex constants. MapLibre cannot read a CSS custom
// property, so portalMapTokens.js resolves them to strings and this file asks
// for them AT DRAW TIME rather than at import time — see that file for why the
// difference matters and what happens when a token is missing.
//
// WHAT MOVED AND WHAT DID NOT:
//   pad       #38bdf8 -> #2DD4BF   the mock
//   road      #fbbf24 -> #FFA83D   the mock
//   other     #a3a3a3 unchanged    decided; measured against the collision below
//   untagged  #e2e8f0 unchanged    decided
//   markup    #f472b6 unchanged    decided
//
// The two that moved were measured first, on the mock's own imagery:
// minimum pairwise CIEDE2000 across all five roles is 17.75 BEFORE and 17.75
// AFTER, because the binding pair is other/untagged and neither moved. The
// mock's values widen pad/other (22.5 -> 25.7) and pad/untagged (23.9 -> 26.3)
// and narrow road/other (30.0 -> 27.9). Nothing approaches the collision.

/** True for null, for an absent property, and for any value outside the vocabulary. */
const IS_UNTAGGED = ["!", ["in", ["get", "submitter_tag"], ["literal", [...SUBMITTER_TAGS]]]];

/**
 * The submitter's own tags, coloured. The fallback is UNTAGGED, not `other`.
 *
 * A FUNCTION, because the colours are resolved per draw. The shape of the
 * expression — three explicit arms and untagged as the fallback — is the fix
 * from the collision described above and is not affected by where the values
 * come from.
 */
const tagColour = (c) => [
  "match", ["get", "submitter_tag"],
  "pad", c.pad,
  "road", c.road,
  "other", c.other,
  c.untagged,
];

/** No fill until something has been asserted about the shape. */
const FILL_OPACITY = ["case", IS_UNTAGGED, 0, 0.25];

/** Dashed while untagged. `line-dasharray` is cross-faded-data-driven — verified
 *  against the v8 spec rather than assumed, because most cross-faded properties
 *  are not. */
const LINE_DASH = ["case", IS_UNTAGGED, ["literal", [2, 2]], ["literal", [1, 0]]];

// ── SELECTION IS NOT A COLOUR ───────────────────────────────────────────────
//
// Colour is already carrying the TAG, which is the assertion the submitter is
// making. If selection also moved colour, a selected `pad` and an unselected
// something-else could look alike, and the one channel that means "what I said
// about this shape" would mean two things.
//
// So selection moves WIDTH and RADIUS. `["boolean", …, false]` rather than the
// bare feature-state lookup: feature state is undefined for every feature until
// something sets it, and an undefined in a `case` predicate throws at
// evaluation rather than reading as false.
const IS_SELECTED = ["boolean", ["feature-state", "selected"], false];

/** Exported so the panel and the tests share one name for the state key. */
export const SELECTED_STATE_KEY = "selected";

/** GeoJSON FeatureCollection from route 6's `features`. */
function toCollection(features = []) {
  return {
    type: "FeatureCollection",
    features: features
      .filter((f) => f?.geometry)
      .map((f) => ({
        type: "Feature",
        // The ordinal, the tag and the label — the same three the export
        // carries. NOT the row id: the map has no use for a uuid and the
        // export deliberately carries none either.
        // `?? null` explicitly: a PREVIEW feature has no submitter_tag at all,
        // and IS_UNTAGGED must read the same for an absent key and an explicit
        // null. Both are covered, but stating the null keeps the property list
        // stable between preview and a submitted revision.
        properties: { ordinal: f.ordinal, submitter_tag: f.submitter_tag ?? null, label: f.label ?? null },
        geometry: f.geometry,
      })),
  };
}

/** Idempotent: safe to call again when a new revision lands. */
export function addSubmissionLayers(map, features) {
  const c = geometryColours();
  const TAG_COLOUR = tagColour(c);
  const data = toCollection(features);
  const existing = map.getSource(SUBMISSION_SOURCE);
  if (existing) { existing.setData(data); return; }

  // promoteId lifts `ordinal` into the feature id, which is what
  // setFeatureState addresses. Nothing sets feature state yet -- the selection
  // model lands with the tag control -- but a source's id strategy cannot be
  // changed by setData, only by removing and re-adding the source, so it is set
  // once here rather than churned later.
  map.addSource(SUBMISSION_SOURCE, { type: "geojson", data, promoteId: "ordinal" });
  // Exported so the map component can wire clicks to exactly the layers that
  // draw a feature, rather than hit-testing the whole map.

  map.addLayer({
    id: `${SUBMISSION_SOURCE}-fill`, type: "fill", source: SUBMISSION_SOURCE,
    filter: ["==", ["geometry-type"], "Polygon"],
    paint: { "fill-color": TAG_COLOUR, "fill-opacity": FILL_OPACITY },
  });
  map.addLayer({
    id: `${SUBMISSION_SOURCE}-line`, type: "line", source: SUBMISSION_SOURCE,
    paint: {
      "line-color": TAG_COLOUR,
      "line-width": ["case", IS_SELECTED, 4, 2],
      "line-dasharray": LINE_DASH,
    },
  });
  map.addLayer({
    id: `${SUBMISSION_SOURCE}-point`, type: "circle", source: SUBMISSION_SOURCE,
    filter: ["==", ["geometry-type"], "Point"],
    paint: {
      "circle-color": TAG_COLOUR,
      "circle-radius": ["case", IS_SELECTED, 8, 5],
      // A point has no outline to thicken, so selection also gets a ring.
      "circle-stroke-width": ["case", IS_SELECTED, 2, 0],
      "circle-stroke-color": c.halo,
    },
  });
}

// ── THE REVIEWER'S OWN ANNOTATIONS ──────────────────────────────────────────
//
// A SEPARATE SOURCE AND SEPARATE LAYERS, not a fourth arm on TAG_COLOUR.
//
// §3.22's collision was a fallback quietly shared between two meanings —
// `other` and untagged rendering identically because one of them had no arm of
// its own. Adding markup to the same expression would put a FIFTH meaning on
// the one channel that already carries the submitter's assertion, and the
// failure mode is the same shape: two different things, one colour, nobody
// notices until a person looks at a real map.
//
// These are also a different KIND of thing. The submitter's geometry is the
// plan; a finding's geometry is a remark about it. They must not look like
// siblings, and markup always draws ON TOP -- a finding hidden under the shape
// it is about is not a finding.
//
// MARKUP GEOMETRY IS OPTIONAL and usually null (checkMarkupInput: "most
// findings are about the feature as submitted"), so toMarkupCollection drops
// entries without one. A finding with no geometry is a row in the list, which
// is a complete rendering of it.

export const MARKUP_SOURCE = "portal-markup";

export const MARKUP_LAYER_IDS = Object.freeze([
  `${MARKUP_SOURCE}-fill`,
  `${MARKUP_SOURCE}-line`,
  `${MARKUP_SOURCE}-point`,
]);

// The markup accent is deliberately outside the submitter palette and is one of
// the three values the mock does not name. It comes from --geom-markup, at the
// value it has always had.

function toMarkupCollection(markup = []) {
  return {
    type: "FeatureCollection",
    features: markup
      .filter((m) => m?.geometry)
      .map((m) => ({
        type: "Feature",
        properties: {
          markup_id: m.id,
          markup_type: m.markup_type,
          feature_ordinal: m.feature_ordinal,
          // THE NOTE, for the label. It was not here: the source carried the
          // four keys the paint expressions read and nothing else, so a Point
          // finding drew a circle with no text available to put beside it.
          //
          // It is the RAW note. Truncation belongs to the label
          // (portalMarkupLabels.js), because the list renders the same note in
          // full and a source that carried a pre-cut string would make the map
          // the authority on what a finding says.
          note: m.note ?? null,
          // Frozen means SENT. A reviewer needs to see which of their own
          // findings the submitter can already read, and the list says so in
          // words -- this carries it so the map can too without a second query.
          frozen: m.frozen_at != null,
        },
        geometry: m.geometry,
      })),
  };
}

/** Idempotent, like addSubmissionLayers: safe to call again when markup changes. */
export function addMarkupLayers(map, markup) {
  const c = geometryColours();
  const COLOUR_MARKUP = c.markup;
  const data = toMarkupCollection(markup);
  const existing = map.getSource(MARKUP_SOURCE);
  if (existing) { existing.setData(data); return; }

  map.addSource(MARKUP_SOURCE, { type: "geojson", data, promoteId: "markup_id" });
  map.addLayer({
    id: `${MARKUP_SOURCE}-fill`, type: "fill", source: MARKUP_SOURCE,
    filter: ["==", ["geometry-type"], "Polygon"],
    paint: { "fill-color": COLOUR_MARKUP, "fill-opacity": 0.18 },
  });
  map.addLayer({
    id: `${MARKUP_SOURCE}-line`, type: "line", source: MARKUP_SOURCE,
    paint: {
      "line-color": COLOUR_MARKUP,
      "line-width": 2,
      // Dashed while UNSENT, solid once frozen. The same "not final yet" idiom
      // untagged uses on the submitter's side, applied to the other party.
      "line-dasharray": ["case", ["boolean", ["get", "frozen"], false],
        ["literal", [1, 0]], ["literal", [3, 2]]],
    },
  });
  map.addLayer({
    id: `${MARKUP_SOURCE}-point`, type: "circle", source: MARKUP_SOURCE,
    filter: ["==", ["geometry-type"], "Point"],
    paint: { "circle-color": COLOUR_MARKUP, "circle-radius": 5,
             "circle-stroke-width": 1, "circle-stroke-color": c.backdrop },
  });
}

/** Fit to what was submitted. Silent when there is nothing to fit. */
export function fitToFeatures(map, features) {
  const coords = [];
  const walk = (c) => {
    if (typeof c?.[0] === "number") { coords.push(c); return; }
    (c ?? []).forEach(walk);
  };
  for (const f of features ?? []) walk(f?.geometry?.coordinates);
  if (coords.length === 0) return;

  const lons = coords.map((c) => c[0]), lats = coords.map((c) => c[1]);
  map.fitBounds(
    [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
    { padding: 48, duration: 0, maxZoom: 17 }
  );
}
